/* Copyright (c) 2019-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, OnDestroy, Injector } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { isEmpty, isUndefined } from 'lodash';
import { formatLogString } from 'utils/lib/common/common-utils';
import { GfcEventsEndpointsService } from 'gfc-events/lib/sdks/gfc-events-endpoints.service';
import { BrowserLeanModeService } from '../../browser-lean-mode-service/browser-lean-mode.service';
import { NetworkStatusService } from '../../network-status-service/network-status.service';
import { GfcAttachmentRequestStatusCodes, ChunkConfig } from '../models/common.constants';
import { LogChunk, LogChunkParams, WebWorkerTimerNotificationData, FeedbackDef } from '../models/common.interfaces';

const RemoteLogsSaveKey = 'RemoteLogsSaveKey';

@Injectable({
    providedIn: 'root'
})
export class LogQueueService implements OnDestroy {
    private logsBuffer = '';
    private chunksQueue = []; // Contains an array of chunks of strings
    private maxBufferSizeInBytes = ChunkConfig.bufferSize;
    private chunkCreationTimeout = ChunkConfig.chunkCreationTimeout;
    private readonly logsChunkUpdated$ = new Subject<LogChunk>();
    private chunksQueueSize = ChunkConfig.chunksQueueSize;
    private chunkResendRetryLimit = ChunkConfig.chunkResendRetryLimit;
    private chunkResendRetryMap: Map<string, number>;
    private isUserConsentAvailable: boolean;
    private logBufferingAllowed = true;
    private gfcService: GfcEventsEndpointsService;
    private leanModeLogger = false;
    private isGfcHeadRequestSent = false;
    private timerSub$ = new Subject<WebWorkerTimerNotificationData>();
    private isTimerTriggered = false;
    private readonly refreshFeedbackSessionSubject = new Subject<number>();
    private timeBasedLogClearanceCounter = 0;
    private feedbackSessionClosed = false;
    private newLine = '\n';
    public chunkSendInProgress = false;
    private eventData: FeedbackDef;

    constructor(
        private datePipe: DatePipe,
        private networkStatusService: NetworkStatusService,
        private injector: Injector,
        private browserLeanModeService: BrowserLeanModeService
    ) {}

    initialize() {
        this.chunkResendRetryMap = new Map<string, number>();
        this.addLogger('LogQueueService', ['initialize']);
        this.networkStatusService.onlineStatusChanged.subscribe(this.onNetworkStatusChanged.bind(this));
        this.browserLeanModeService.leanModeTransitionState.subscribe(leanMode => {
            this.leanModeLogger = leanMode;
            if (leanMode) {
                this.stopTimeBasedLogCollection();
            }
        });
    }

    /**
     * A local logger implementation from utils is used as LoggingService cannot be used in
     * log-collector so as to avoid cyclic dependency
     */
    addLogger(serviceName: string, logArgs?: any[]) {
        const logString = formatLogString(serviceName, 'INFO', this.datePipe, logArgs);
        this.appendLogEntry(logString);
    }

    setHeadRequestStatus(requestSent: boolean) {
        this.isGfcHeadRequestSent = requestSent;
    }

    private checkFeasibilityAndSendChunk(logString: string, isLastChunk = false, params?: LogChunkParams) {
        if (
            this.isUserConsentAvailable &&
            this.isGfcHeadRequestSent &&
            !isLastChunk &&
            !this.chunkSendInProgress &&
            !isEmpty(logString)
        ) {
            this.logsChunkUpdated$.next({ logString: this.sendChunkToServer(logString), isLastChunk: false });
        } else if (this.isUserConsentAvailable && isLastChunk) {
            // If its the last chunk, send it irrespective of whether a previous chunk send is in progress.
            // Also, send, even if logstring is empty, because the EOB event needs to be sent regardless.
            this.logsChunkUpdated$.next({
                logString: this.sendChunkToServer(logString),
                isLastChunk,
                params
            });
        }
    }

    private startTimeBasedLogDispatch() {
        // Attempt to clear buffer if it has been X mins last chunk creation
        this.addLogger('LogQueueService', [
            'Interval of 10 mins elapsed: current buffer length: ',
            this.logsBuffer.length
        ]);
        this.addLogger('LogQueueService', ['Chunk send in progress: ', this.chunkSendInProgress]);

        if (this.chunkSendInProgress === false) {
            this.refreshFeedbackSessionSubject.next(++this.timeBasedLogClearanceCounter);
            this.addLogger('LogQueueService', ['Clearing chunk on timeout... ']);
            const logsToBeSent = this.extractAndSaveChunk();
            this.checkFeasibilityAndSendChunk(logsToBeSent);
        }
    }

    private stopTimeBasedLogCollection(msg = '') {
        this.isTimerTriggered = false;
        this.timerSub$.next({ startTimer: false, timeout: 0 });
        this.addLogger('LogQueueService', ['Stopping log collection timer. ' + msg]);
    }

    private isBufferSizeWithinLimits(currentBuffer: string, newLogString: string): boolean {
        const size = currentBuffer.concat(newLogString, this.newLine).length;
        return size > this.maxBufferSizeInBytes ? false : true;
    }

    private sendChunkToServer(logsChunk: string): string {
        this.chunkSendInProgress = true;
        return logsChunk ? logsChunk : '';
    }

    private addChunksToQueue(chunk: string) {
        if (this.chunksQueue.length === this.chunksQueueSize) {
            this.chunksQueue.splice(0, this.chunksQueue.length);
        }
        this.chunksQueue.push(chunk);
        this.chunkResendRetryMap.set(this.chunksQueue[this.chunksQueue.length - 1], 0);
    }

    private extractAndSaveChunk(): string {
        // Extract and push chunk into the backup queue to try resending later if HTTP send fails
        // and clear logs from logsBuffer
        const chunk = this.logsBuffer.slice(0);
        this.addChunksToQueue(chunk);
        this.logsBuffer = '';
        return this.popChunkFromQueue();
    }

    private pushLogToBuffer(log: string) {
        this.logsBuffer = this.logsBuffer.concat(log, this.newLine);
    }

    private startTimer() {
        if (!this.leanModeLogger) {
            this.timerSub$.next({ startTimer: true, timeout: this.chunkCreationTimeout });
            this.isTimerTriggered = true;
        }
    }

    /**
     * Get the first element from the queue but do not delete it as yet
     */
    private popChunkFromQueue(): string {
        const currentChunkSendAttemptCount = this.chunkResendRetryMap.get(this.chunksQueue[0]);

        if (
            this.isUserConsentAvailable &&
            this.isGfcHeadRequestSent &&
            currentChunkSendAttemptCount <= this.chunkResendRetryLimit
        ) {
            // Increment the retry count because we are going to attempt a chunk send or a resend
            this.chunkResendRetryMap.set(this.chunksQueue[0], currentChunkSendAttemptCount + 1);
            return this.chunksQueue.slice(0, 1).toString();
        } else if (
            this.isUserConsentAvailable &&
            this.isGfcHeadRequestSent &&
            currentChunkSendAttemptCount > this.chunkResendRetryLimit
        ) {
            this.removeChunkFromSavedQueue();
            return '';
        } else {
            // Don't attempt to send any chunk as we do not have user consent yet
            return '';
        }
    }

    public saveLogsToSessionStorage() {
        this.addChunksToQueue(this.logsBuffer);
        const allLogs = JSON.stringify(this.chunksQueue);
        sessionStorage.setItem(RemoteLogsSaveKey, allLogs);
    }

    public readConsoleLogsFromSessionStorage() {
        const logsFromStorage = JSON.parse(sessionStorage.getItem(RemoteLogsSaveKey));
        sessionStorage.removeItem(RemoteLogsSaveKey);
        // SessoinStorage only supports saving data in string format, so all the log chunks collected before login will be
        // merged in single string and saved as single string. JSON.parse converts back single string to same array of strings before login,
        // 'appendPreviousLogs' function then appends chunks into present 'this.chunksQueue' array.
        this.appendPreviousLogs(logsFromStorage);
    }

    private appendPreviousLogs(chunks: string[]) {
        if (this.chunkResendRetryMap) {
            this.chunkResendRetryMap.clear();
        }
        chunks = chunks.concat(this.chunksQueue);
        if (chunks.length > this.chunksQueueSize) {
            this.chunksQueue = chunks.slice(-this.chunksQueueSize);
        } else {
            this.chunksQueue = chunks;
        }

        for (let i = 0; i < this.chunksQueue.length; i++) {
            this.chunkResendRetryMap.set(this.chunksQueue[i], 0);
        }
    }

    /**
     * First element from the queue can be deleted once successfully sent to the server
     */
    private removeChunkFromSavedQueue() {
        this.chunkResendRetryMap.delete(this.chunksQueue[0]);
        this.chunksQueue.splice(0, 1);
    }

    /**
     * Handle Offline states
     */
    private onNetworkStatusChanged(isOnline: boolean) {
        if (!isOnline) {
            this.addLogger('LogQueueService', ['LogCollector lib is offline right now']);
            this.stopTimeBasedLogCollection();
        } else {
            this.addLogger('LogQueueService', ['LogCollector lib is online right now']);
        }
    }

    /**
     * If a new feedback session is in the process of being initiated from remote-logging.service after an idle span
     * then we pause the sending of chunks from here until a new feedback HEAD request has been successfully sent
     * which allows further sending of chunks
     *
     * @param isSessionClosed indicates whether an active feedback session is available to send log chunks
     */
    updateFeedbackSessionStatus(isSessionClosed: boolean) {
        this.feedbackSessionClosed = isSessionClosed;
    }

    getRefreshFeedbackSubjectObservable(): Observable<number> {
        return this.refreshFeedbackSessionSubject.asObservable();
    }

    onLogsChunkUpdated(): Observable<LogChunk> {
        return this.logsChunkUpdated$.asObservable();
    }

    /**
     * This method will be called on browser tab closure or window unload
     * Here, pending queued chunks (if any) and logs in the logsBuffer
     * should be concatenated and sent as a part of the last chunk
     */
    clearQueueAndBuffer(isBrowserClosing: boolean) {
        // storing the previous chunk state before updating chunkSendInProgress
        const prevChunkInProgress = this.chunkSendInProgress;
        this.chunkSendInProgress = true;
        this.gfcService = isUndefined(this.gfcService) ? this.injector.get(GfcEventsEndpointsService) : this.gfcService;
        let allChunks = '';
        this.chunksQueue.forEach(chunk => {
            allChunks = allChunks.concat(chunk, this.newLine);
        });
        allChunks = allChunks.concat(this.logsBuffer, this.newLine);
        if (this.logBufferingAllowed && this.isUserConsentAvailable) {
            // If its the last chunk, send it irrespective of whether a previous chunk send is in progress.
            // This is because the EOB event needs to be sent regardless.
            if (isBrowserClosing) {
                allChunks = allChunks.concat('sendBeacon api is going to be called.');
                this.gfcService.sendLastFeedbackChunkEvent(allChunks, this.eventData);
            } else {
                allChunks = allChunks.concat('eob request will be sent for the last chunk.');
                // If its not a browser closure scenario then use the existing async flow to
                // send the chunks sequentially one after the other
                this.checkFeasibilityAndSendChunk(allChunks, true, { isBrowserClosing, prevChunkInProgress });
            }
        }
        this.logsBuffer = '';
        this.chunksQueue = [];
        this.chunkSendInProgress = false;
        this.eventData = null;
    }

    /**
     * Add log string to log buffer
     */
    appendLogEntry(log: string) {
        if (this.logBufferingAllowed) {
            if (this.isBufferSizeWithinLimits(this.logsBuffer, log)) {
                this.pushLogToBuffer(log);
            } else if (this.feedbackSessionClosed) {
                // feedbackSessionClosed: If there is no active feedback upload session in progress, then push the log into buffer
                // and initiate a new feedback session
                this.feedbackSessionClosed = false;
                // Reset timeBasedLogClearanceCounter when logs are cleared due to buffer limit
                this.timeBasedLogClearanceCounter = 0;
                this.refreshFeedbackSessionSubject.next(this.timeBasedLogClearanceCounter);
                this.pushLogToBuffer(log);
            } else {
                // Reset timeBasedLogClearanceCounter when logs are cleared due to buffer limit
                this.timeBasedLogClearanceCounter = 0;
                if (this.isTimerTriggered) {
                    // Stop the timer if is already running, since log chunk is being cleared due to buffer limit
                    this.stopTimeBasedLogCollection('due to buffer limit.');
                }
                const logsToBeSent = this.extractAndSaveChunk();
                this.pushLogToBuffer(log);
                this.checkFeasibilityAndSendChunk(logsToBeSent);
            }
        }
    }

    /**
     * RemoteLoggingService which makes the http call should notify here
     */
    onSendChunkSuccess() {
        this.chunkSendInProgress = false;
        this.removeChunkFromSavedQueue();
        if (this.chunksQueue.length !== 0) {
            // Try sending all pending chunks one after the other
            this.checkFeasibilityAndSendChunk(this.popChunkFromQueue());
        } else {
            // No more pending chunks to clear, thus start timer for next buffer clearance
            this.startTimer();
        }
    }

    /**
     * RemoteLoggingService which makes the http call should notify here
     */
    onSendChunkFailure(error: any) {
        this.chunkSendInProgress = false;
        // Retry sending chunks in some cases
        switch (error.status) {
            case GfcAttachmentRequestStatusCodes.NonSequentialChunk: // TODO Read the X-Data-Offset header in the response and try resending
            case GfcAttachmentRequestStatusCodes.FrameChecksumOrSizeMismatch:
            case GfcAttachmentRequestStatusCodes.ServerError:
                this.checkFeasibilityAndSendChunk(this.popChunkFromQueue());
                break;
            default:
                console.log('Error encountered in sending chunks: ', error);
                // Treat any unknown 4xx and 5xx as errors and attempt resending
                if (error.status !== GfcAttachmentRequestStatusCodes.InvalidFeedbackId) {
                    this.checkFeasibilityAndSendChunk(this.popChunkFromQueue());
                }
                break;
        }
    }

    /**
     * This helps terminate the propagation of log chunks to the server
     *
     * @param allowLogBuffering if true, then logs can still be saved in the buffer,
     * else, we stop saving logs - this is to avoid memory bloating in case of multiple chunk send scenarios
     * Sort of a fail safe measure.
     */
    stopChunkCollection(allowLogBuffering = false) {
        this.logBufferingAllowed = allowLogBuffering;
        this.stopTimeBasedLogCollection(
            'Going to stop chunk collection. logBufferingAllowed: ' + this.logBufferingAllowed + '.'
        );
        this.clearQueueAndBuffer(false);
    }

    setBufferConfig(config: any) {
        this.chunkCreationTimeout = config.msBetweenChunkCreation;
        this.maxBufferSizeInBytes = config.maxBufferSizeInBytes;
        this.chunksQueueSize = config.chunksQueueSize;
        this.chunkResendRetryLimit = config.chunkResendRetryLimit;
    }

    updateUserConsent(userConsent: boolean) {
        this.isUserConsentAvailable = userConsent;
    }

    updateEventData(eventData: FeedbackDef) {
        this.eventData = { ...eventData };
    }

    timerSubscriptionChanged(): Observable<WebWorkerTimerNotificationData> {
        return this.timerSub$.asObservable();
    }

    // Notified here by gfcHelperService based on timer (web worker) response
    updateTimer(timerResponse: string) {
        if (timerResponse === 'Timeout') {
            this.startTimeBasedLogDispatch();
        } else if (timerResponse === 'Cleared') {
            this.addLogger('LogQueueService', ['Log collection timer has been stopped']);
        }
    }

    ngOnDestroy() {
        this.logsBuffer = '';
    }
}
